export const OBSERVATION_CATEGORIES = [
  "Structural",
  "MEP / Services",
  "Envelope",
  "Finishes",
  "Site / Civil",
  "Other",
];

export const OBSERVATION_SEVERITIES = [
  { id: "minor", labelKey: "drawings.observation.severity.minor", color: "#f1a019", apiValue: "1" },
  { id: "major", labelKey: "drawings.observation.severity.major", color: "#f4690f", apiValue: "2" },
  { id: "critical", labelKey: "drawings.observation.severity.critical", color: "#e5484d", apiValue: "3" },
];

export const OBSERVATION_TYPES = [
  {
    kind: "photo",
    labelKey: "drawings.observation.type.photo",
    descKey: "drawings.observation.type.photo_desc",
    tint: "#2ea66b",
  },
  {
    kind: "deviation",
    labelKey: "drawings.observation.type.deviation",
    descKey: "drawings.observation.type.deviation_desc",
    tint: "#e5484d",
  },
  {
    kind: "note",
    labelKey: "drawings.observation.type.note",
    descKey: "drawings.observation.type.note_desc",
    tint: "#8b5cf6",
  },
  {
    kind: "checklist",
    labelKey: "drawings.observation.type.checklist",
    descKey: "drawings.observation.type.checklist_desc",
    tint: "#2b6ef0",
  },
];

export const CHECKLIST_PIN_COLOR = "#2b6ef0";

export const parseModuleMeta = (module) => {
  if (!module?.path_data) return {};
  try {
    return JSON.parse(module.path_data);
  } catch {
    return {};
  }
};

export const stringifyModuleMeta = (meta) => JSON.stringify(meta || {});

export const observationKindFromModule = (module) => {
  if (
    module?.module_type === "photo" ||
    module?.module_type === "note" ||
    module?.module_type === "checklist"
  ) {
    return module.module_type;
  }
  if (module?.deviation_id || module?.deviation) {
    return "deviation";
  }
  const meta = parseModuleMeta(module);
  if (meta.kind) return meta.kind;
  return null;
};

export const severityColor = (severityId) =>
  OBSERVATION_SEVERITIES.find((item) => item.id === severityId)?.color || "#e5484d";

/** Map API severity (1/2/3 or id string) to observation severity id. */
export const severityIdFromValue = (value) => {
  if (value == null || value === "") return null;
  const asString = String(value);
  const byId = OBSERVATION_SEVERITIES.find((item) => item.id === asString);
  if (byId) return byId.id;
  const byApi = OBSERVATION_SEVERITIES.find((item) => item.apiValue === asString);
  return byApi?.id || null;
};

export const OBSERVATION_PIN = { width: 32, height: 44 };

export const moduleVisibleOnPage = (module, page) => {
  const meta = parseModuleMeta(module);
  if (typeof meta.page !== "number") return true;
  return meta.page === page;
};

export const isInspectableModule = (module) => {
  if (!module) return false;
  const kind = observationKindFromModule(module);
  return kind === "photo" || kind === "note" || kind === "deviation" || kind === "checklist";
};

/** Observation / document pins stay locked after save unless relocating. */
export const isLockedObservationPin = (module) => {
  if (!module || module._localPending) return false;
  const kind = observationKindFromModule(module);
  return kind === "photo" || kind === "note" || kind === "deviation" || kind === "checklist";
};

export const deviationIdFromModule = (module) => {
  if (!module) return null;
  if (module.deviation?.id != null) return module.deviation.id;
  if (module.deviation_id != null) return module.deviation_id;
  const meta = parseModuleMeta(module);
  return meta.deviation_id ?? null;
};

export const checklistIdFromModule = (module) => {
  if (!module) return null;
  if (module.checklist_id != null) return module.checklist_id;
  const meta = parseModuleMeta(module);
  return meta.checklist_id ?? null;
};

export const checklistItemIdFromModule = (module) => {
  if (!module) return null;
  if (module.checklist_item_id != null) return module.checklist_item_id;
  const meta = parseModuleMeta(module);
  return meta.checklist_item_id ?? null;
};

/** Resolve checklist item title/status for photo pins linked to a checklist item. */
export const resolveChecklistItemContext = (module, checklists = []) => {
  if (!module) return null;
  const meta = parseModuleMeta(module);
  const checklistId = checklistIdFromModule(module);
  const checklistItemId = checklistItemIdFromModule(module);
  if (checklistId == null && checklistItemId == null && !module.checklist_item) {
    return null;
  }

  const checklist =
    module.checklist ||
    (checklists || []).find((c) => String(c.id) === String(checklistId)) ||
    null;

  const itemFromList =
    (checklist?.items || []).find(
      (row) => String(row.id) === String(checklistItemId)
    ) || null;

  const item = module.checklist_item || itemFromList || null;

  // Prefer live item title; path_data.checklist_item_title for pins; avoid document-name fallbacks like `checklist item "2"`.
  const metaTitle =
    meta.checklist_item_title ||
    (meta.title && !/^checklist item\b/i.test(String(meta.title))
      ? meta.title
      : null);
  const itemTitle = item?.displayTitle || item?.title || metaTitle || null;

  return {
    checklist,
    item,
    checklistId: checklistId || checklist?.id || null,
    checklistItemId: checklistItemId || item?.id || null,
    checklistTitle:
      checklist?.displayTitle ||
      checklist?.title ||
      (checklist?.number != null ? `#${checklist.number}` : null),
    itemTitle,
  };
};

export const documentIdFromModule = (module) => {
  if (!module) return null;
  const meta = parseModuleMeta(module);
  return meta.document_id ?? null;
};

/** Photo pins on this drawing that already have a stored document. */
export const existingDrawingPhotoPins = (modules = []) =>
  (modules || []).filter((mod) => {
    if (!mod || mod._localPending) return false;
    if (observationKindFromModule(mod) !== "photo") return false;
    return documentIdFromModule(mod) != null;
  });

export const findModuleByDocumentId = (modules, documentId) => {
  if (documentId == null) return null;
  return (modules || []).find(
    (mod) => String(documentIdFromModule(mod)) === String(documentId)
  ) || null;
};

/** Counts for checklist pin badges (from related checklist payload). */
export const checklistPinStats = (module, checklists) => {
  const checklistId = checklistIdFromModule(module);
  if (checklistId == null) return null;
  const checklist =
    module.checklist ||
    (checklists || []).find((c) => String(c.id) === String(checklistId));
  if (!checklist) return null;

  const items = checklist.items || [];
  const approved = items.filter((item) => item?.isApproved).length;
  const deviations = items.filter((item) => item?.isDeviation).length;
  const na = items.filter((item) => item?.is_not_applicable).length;
  const resolved = approved + deviations + na;
  const total = items.length;
  const status = String(checklist.status || "").toLowerCase();
  const completed =
    (total > 0 && resolved === total) ||
    status === "finished" ||
    status === "approved" ||
    status === "closed" ||
    checklist.finished === true ||
    checklist.approved === true;

  return {
    checklist,
    approved,
    deviations,
    na,
    resolved,
    total,
    completed,
    hasDeviation: deviations > 0 || status === "deviation",
  };
};

export const linkedChecklistIdsFromModules = (modules) => {
  const ids = new Set();
  (modules || []).forEach((mod) => {
    const kind = observationKindFromModule(mod);
    const checklistId = checklistIdFromModule(mod);
    if (checklistId == null) return;
    if (kind === "checklist" || kind === "photo") {
      ids.add(Number(checklistId));
    }
  });
  return ids;
};

export const directlyPinnedChecklistIds = (modules) => {
  const ids = new Set();
  (modules || []).forEach((mod) => {
    if (observationKindFromModule(mod) !== "checklist") return;
    const checklistId = checklistIdFromModule(mod);
    if (checklistId != null) ids.add(Number(checklistId));
  });
  return ids;
};

export const stubChecklistFromModules = (modules, checklistId) => {
  if (checklistId == null) return null;
  const id = Number(checklistId);
  for (const mod of modules || []) {
    if (Number(checklistIdFromModule(mod)) !== id) continue;
    if (mod.checklist && (mod.checklist.id != null || mod.checklist.items)) {
      return {
        ...mod.checklist,
        id: mod.checklist.id ?? id,
      };
    }
  }
  return null;
};

export const flattenChecklistItems = (checklists) => {
  const rows = [];
  (checklists || []).forEach((checklist) => {
    const checklistTitle =
      checklist.displayTitle || checklist.title || checklist.number || `#${checklist.id}`;
    (checklist.items || []).forEach((item) => {
      rows.push({
        checklistId: checklist.id,
        checklistTitle,
        itemId: item.id,
        itemTitle: item.displayTitle || item.title || `#${item.id}`,
      });
    });
  });
  return rows;
};
